/**
 * Type for options that can be matched case-insensitively by name
 */
type OptionWithName = { name: string; [key: string]: unknown };

/**
 * Finds an option in a list by matching the name case-insensitively.
 * Trims whitespace from both the value and option names before comparing.
 *
 * @param options - Array of options with at least a 'name' property
 * @param value - The value to search for (case-insensitive)
 * @returns The matching option or undefined if not found
 *
 * @example
 * const options = [
 *   { name: 'Laptop', value: '1' },
 *   { name: 'Desktop', value: '2' }
 * ];
 * findOptionCaseInsensitive(options, 'laptop'); // Returns { name: 'Laptop', value: '1' }
 * findOptionCaseInsensitive(options, 'DESKTOP'); // Returns { name: 'Desktop', value: '2' }
 * findOptionCaseInsensitive(options, ''); // Returns undefined
 */
export const findOptionCaseInsensitive = <T extends OptionWithName>(
  options: T[],
  value: string | undefined | null
): T | undefined => {
  if (!value?.trim()) return undefined;

  const normalizedValue = value.trim().toLowerCase();

  return options.find(
    option => option.name.trim().toLowerCase() === normalizedValue
  );
};
